import { BRAND_DEPLOYMENT_STRIP } from '../../../../theme/tokens';

/** The transform + stacking one strip card wears for a given scroll position. */
export type SlotTransform = {
  /** CSS transform for the Slot (gap push, lift, organic rotation, centre scale). */
  transform: string;
  /** z-index — cards closer to the centre ride above their neighbours. */
  z: number;
};

const { SPREAD, GAP_SOFT, CENTER_LIFT, CENTER_SCALE, CENTER_REACH, STRAIGHTEN, LEAN_DEG, LEAN_SOFT } =
  BRAND_DEPLOYMENT_STRIP;

const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));

/**
 * Odd, saturating "spread" curve that is FLAT at the centre: 0 at n=0 with a zero slope there,
 * easing out to ±1 as |n| grows (a signed gaussian shoulder). The zero slope at the centre is
 * what keeps the gap/lean from wobbling as the scroll settles into a snap point.
 */
const spread = (n: number, soft: number): number => Math.sign(n) * (1 - Math.exp(-((n / soft) ** 2)));

/**
 * Continuous, scroll-linked transform for ONE strip card, from its signed distance to the
 * container centre (pure, so it is unit-tested without a DOM). Replaces the old discrete
 * hover/focal `stripSlotStyle`: because every quantity is a smooth function of `delta`, the
 * choreography tracks the scroll frame-by-frame with no threshold jump when the centred card
 * changes.
 *
 * - GAP: neighbours push away from the centre (a saturating gaussian shoulder, FLAT at the
 *   centre), opening a clear gap that frames whichever card is nearest the snap point. The
 *   centred card itself sits un-shifted, and the flatness keeps the snap from wobbling.
 * - ROTATION: the card keeps its organic resting `tilt`, straightened as it nears the centre,
 *   plus a faint directional lean so the strip breathes as you scroll. Pass `straight` to drop
 *   all rotation (every card upright) — the choreography's DEFAULT; the tilt/lean is revealed
 *   only under the `show_in_progress` flag.
 * - LIFT + SCALE: the centred card rises and grows a touch (a soft bell in the distance), for
 *   premium prominence; both ease back to rest by `CENTER_REACH` cards away.
 *
 * @param delta     signed px distance from the container centre (cardCentre - viewportCentre)
 * @param step      px between adjacent card centres — normalises `delta` into "cards"
 * @param tilt      this card's resting organic tilt (deg)
 * @param straight  when true, hold every card upright: no resting tilt, no lean (gap/lift/scale
 *                  are untouched). The choreography passes this by DEFAULT; the tilt/lean style
 *                  is revealed only under the `show_in_progress` flag.
 */
export const slotTransform = (delta: number, step: number, tilt: number, straight = false): SlotTransform => {
  const n = step > 0 ? delta / step : 0; // signed distance in "cards"
  // Soft bell: 1 at the centre, easing to 0 by CENTER_REACH cards away (cosine → premium).
  const bump = (1 + Math.cos(Math.PI * clamp(Math.abs(n) / CENTER_REACH, 0, 1))) / 2;

  const pushX = SPREAD * spread(n, GAP_SOFT);
  const rot = straight ? 0 : tilt * (1 - STRAIGHTEN * bump) + LEAN_DEG * spread(n, LEAN_SOFT);
  const lift = CENTER_LIFT * bump;
  const scale = 1 + CENTER_SCALE * bump;
  // Strictly decreasing with distance, so the centred card always stacks on top.
  const z = 10000 - Math.round(Math.abs(delta));

  return {
    transform: `translate3d(${pushX.toFixed(2)}px, ${(-lift).toFixed(2)}px, 0) rotate(${rot.toFixed(3)}deg) scale(${scale.toFixed(4)})`,
    z,
  };
};

export default slotTransform;
